import React, { useEffect, useState} from 'react';
import {
    arrayOf,
    bool,
    func,
    number,
    object,
    oneOf,
    oneOfType,
    shape,
    string
} from 'prop-types';
import {
    CustomPaging,
    IntegratedSelection,
    PagingState,
    SelectionState,
    SortingState
} from '@devexpress/dx-react-grid';
import {
    Grid,
    PagingPanel,
    Table,
    TableHeaderRow,
    TableSelection,
    VirtualTable
} from '@devexpress/dx-react-grid-material-ui';
import CellRenderer from './CellRenderer';
import clsx from 'clsx';
import MetaContext from './MetaContext';
import NoDataCell from './NoDataCell';

import defaultClasses from './RulesTable.module.css';

const resolution2560 = matchMedia('(min-width:2560px)');
const resolution1980 = matchMedia('(min-width:1980px)');


const COLUMNS = [
    { name: 'name', title: 'Name' },
    { name: 'custom', title: 'K2A/Custom' },
    { name: 'targetCount', title: 'Target Count' },
    { name: 'failureCount', title: 'Failure Count' },
    { name: 'activeFaults', title: 'Active Faults' },
    { name: 'enabled', title: 'Enabled' },
    { name: 'status', title: 'Status' },
    { name: 'action', title: ' ' }
];

/**
 * This column configuration exclused certain columns from being sortable due
 * to them being "frontend-only" columns (such as the "actions" column) or the
 * backend not supporting that column name as a database column name.
 */
const ColumnSortingConfig =  [
    {
        columnName:     'targetCount',
        direction:      null,
        sortingEnabled: false
    },
    {
        columnName:     'failureCount',
        direction:      null,
        sortingEnabled: false
    },
    {
        columnName:     'activeFaults',
        direction:      null,
        sortingEnabled: false
    },
    {
        columnName:     'status',
        direction:      null,
        sortingEnabled: false
    },
    {
        columnName:     'action',
        direction:      null,
        sortingEnabled: false
    }
];

const StyledTable = (props) => (
    <Table.Table {...props} className={defaultClasses.table} />
);

const StyledHeaderCellComponent = (props) => (
    <TableHeaderRow.Cell
        {...props}
        className={clsx(defaultClasses.cell, defaultClasses.headerCell)}
    />
);

const StyledPagingPanel = (props) => (
    <PagingPanel.Container
        {...props}
        className={defaultClasses.pagingPanel}
    />
);

function getRowId({ id }) {
    return id;
}

const RulesTable = ({
    currentPage,
    error,
    isLoading,
    onCurrentPageChange,
    onPageSizeChange,
    onRowSelection,
    onSortingChange,
    pageSize,
    pageSizes,
    readOnly,
    rules,
    selectedIds,
    sorting,
    totalCount
}) => {
    const [tableHeight, setTableHeight] = useState('480px');
    
    const update2560TableHeight = () => {
        setTableHeight('57vh');
    };

    const update1980TableHeight = () => {
        if (window.innerWidth <= 1980) {
            setTableHeight('42vh');
        }
        else {
            setTableHeight('57vh');
        }
    };

    useEffect(() => {
        return () => {
            resolution1980.removeEventListener('change', update1980TableHeight);
            resolution2560.removeEventListener('change', update2560TableHeight);
        };

    }, []);

    useEffect(() => {
        resolution2560.addEventListener('change', update2560TableHeight);
        resolution1980.addEventListener('change', update1980TableHeight);
        update1980TableHeight();
    }, []);

    let noDataMessage = 'There are no Rules';
    if (isLoading) {
        noDataMessage = 'Loading Rules...';
    } else if (error) {
        noDataMessage = <div className={defaultClasses.error}>Error Loading Rules: {error.message || error}</div>;
    }

    return (
        <MetaContext.Provider value={{ error, readOnly }}>
            <Grid
                columns={COLUMNS}
                getRowId={getRowId}
                rows={rules}
            >
                <SelectionState
                    selection={selectedIds}
                    onSelectionChange={onRowSelection}
                />
                <IntegratedSelection />
                <SortingState
                    columnExtensions={ColumnSortingConfig}
                    onSortingChange={onSortingChange}
                    sorting={sorting}
                />
                <PagingState
                    currentPage={currentPage}
                    onCurrentPageChange={onCurrentPageChange}
                    onPageSizeChange={onPageSizeChange}
                    pageSize={pageSize}
                />
                <CustomPaging totalCount={totalCount} />
                <VirtualTable
                    height={tableHeight}
                    cellComponent={CellRenderer}
                    messages={{
                        noData: noDataMessage
                    }}
                    noDataCellComponent={NoDataCell}
                    tableComponent={StyledTable}
                />
                <TableHeaderRow
                    cellComponent={StyledHeaderCellComponent}
                    showSortingControls
                />
                <TableSelection
                    showSelectAll
                />
                <PagingPanel
                    containerComponent={StyledPagingPanel}
                    pageSizes={pageSizes}
                />
            </Grid>
        </MetaContext.Provider>
    );
};

RulesTable.defaultProps = {
    isLoading:       false,
    onRowSelection:  () => {},
    onSortingChange: () => {},
    pageSizes:       [],
    readOnly:        true,
    rules:           [],
    selectedIds:     [],
    sorting:         []
};

RulesTable.propTypes = {
    currentPage:         number,
    error:               oneOfType([object, string]),
    isLoading:           bool,
    onCurrentPageChange: func,
    onRowSelection:      func,
    onSortingChange:     func,
    pageSize:            number,
    pageSizes:           arrayOf(number),
    readOnly:            bool,
    rules:               arrayOf(object),
    selectedIds:         arrayOf(string),
    sorting:             arrayOf(shape({
        columnName: string,
        direction:  oneOf(['asc', 'desc'])
    })),
    totalCount: number
};

export default RulesTable;
